/**
 * #2496 — where the CP auth server sends a sign-in that broke before any identity existed.
 *
 * ⛔ THE ONE REFUSAL PAGE THAT CAN OFFER A RETRY, and the reason is what makes it safe rather than
 * friendly. It is reachable only BEFORE the server has established who the caller is: the provider
 * bounced back with `?error=` (a cancel at a first-time consent screen, or a dismissal of the
 * account chooser where Google returns one), the OAuth state was unknown, expired or already
 * redeemed, the state named a different provider, or the provider's own code exchange failed. At
 * every one of those points the server knows about no account, so there is nothing a retry can
 * disclose and nothing this page can get wrong.
 *
 * ⛔ THE PROVIDER'S `error_description` IS NOT CARRIED HERE. It is a string the provider chose, and
 * rendering it would put third-party text on a page we serve. "Nothing was changed" is the fact the
 * person needs, and it is true on all five branches: nothing about any account changed. ⚠️ Not
 * "nothing was written" — three of the five branches delete the single-use OAuth state record on
 * the way past, which is the flow consuming its own token and not a change to anything the person
 * has.
 *
 * ⚠️ The title is the same sentence `OAuthCallbackPage`'s terminal card uses, deliberately. The two
 * screens are the same event seen from either side of the redirect — a sign-in that did not
 * complete — and giving them different words would make one incident read as two.
 */
import { AuthCardFrame } from '../shell/AuthCardFrame';
import { ButtonLink } from '../shell/ButtonLink';

export function SignInIncompletePage(): React.JSX.Element {
  return (
    <AuthCardFrame
      title="Sign-in didn’t complete"
      /*
       * ⛔ FOCUS MOVES TO THE TITLE ON ARRIVAL (D-1). This card REPLACES the whole page after a
       * top-level redirect the person did not initiate, and a full-page swap is announced
       * reliably by moving focus, not by a live region. `OAuthConsentPage` states the same rule.
       * ⛔ The TITLE, never the action: a card that arrives with focus on `Try again` can be
       * actioned by a keypress aimed at the previous screen.
       */
      autoFocusTitle
      notice={{
        // `bad`, matching `OAuthCallbackPage`'s card for the same title: something the person was
        // doing failed, and they are being asked to do it again.
        verdict: 'bad',
        message: 'Nothing was changed. You can try again.',
      }}
      testId="sign-in-incomplete"
    >
      {/* A real anchor, not a `<button onClick={navigate}>`: `/login` has a URL, so right-click,
          middle-click and copy-link all keep working, and it announces as a link. */}
      <ButtonLink to="/login" tone="primary" size="md" data-testid="sign-in-incomplete-retry">
        Try again
      </ButtonLink>
    </AuthCardFrame>
  );
}
